import { useCallback, useEffect, useRef, useState } from 'react';
import { voiceApi, VoiceTtsAvailability } from '../../../services/voiceApi';
import {
  apiQueueFromSegments,
  clauseSegments,
  expandSegments,
  gapAfterRun,
  parseSpeechText,
  pickPortugueseVoice,
  proseRunAccepts,
  softenApiProsody,
  speakableNormYear,
  SpeechSegment,
  speechSentences,
} from './browserSpeech';
import { endChunk, pulseBoundary, resetSpeechBands, startChunk } from './speechPlaybackBands';
import {
  fallbackChainFor,
  getPreferredVoiceProfileId,
  getVoiceProfile,
  GreeningVoiceProfile,
  PADRAO2_FISH_ISABELA,
  PADRAO3_BROWSER,
} from './voiceProfiles';

export interface SpeechCursor {
  index: number;
  fraction: number;
  /** Fração já ouvida dentro da seção atual. */
  ratio: number;
  section?: string;
}

export interface SpeechProgress extends SpeechCursor {
  total: number;
  overall: number;
}

/** Repete a palavra já alcançada. Não salta para a seguinte. */
function sliceRemaining(text: string, fraction: number): string {
  if (fraction <= 0.04) return text;
  if (fraction >= 0.97) return '';
  const at = Math.min(text.length - 1, Math.floor(text.length * fraction));
  const space = text.lastIndexOf(' ', at);
  const cut = space >= 0 ? space + 1 : 0;
  return text.slice(cut).trim();
}

const GREENING1_BRAND = 'Greening eiiAiii';
const GREENING2_BRAND = 'Greening êiái';

function speakableBrand(text: string): string {
  return speakableNormYear(text.replace(/Greening(?:\. ?)?AI/g, GREENING1_BRAND));
}

/** A Greening1 já acerta o nome. A Greening2 fala êiái junto, separado do nome. A pontuação não vai para a API. */
function utteredText(text: string, profileId: string): string {
  const branded = profileId === PADRAO2_FISH_ISABELA.id
    ? text.replace(/Greening eiiAiii/g, GREENING2_BRAND)
    : text;
  return softenApiProsody(branded);
}

interface SpeechRun {
  end: number;
  text: string;
  pauseAfterMs: number;
  spans: Array<{ index: number; start: number; end: number; baseFraction: number }>;
}

function ownClip(segment?: SpeechSegment): boolean {
  return Boolean(segment?.listHeader || segment?.listItem || /\?\s*$/.test(segment?.text.trim() || ''));
}

/** Frases com pausa zero viram um áudio, até duas frases ou cerca de 420 caracteres. */
function speechRun(queue: SpeechSegment[], index: number, firstText?: string, baseFraction = 0): SpeechRun {
  let joined = '';
  const spans: SpeechRun['spans'] = [];
  let cursor = index;
  while (cursor < queue.length) {
    const segment = queue[cursor];
    const piece = cursor === index && firstText != null ? firstText : segment.text;
    if (spans.length && (ownClip(segment) || !proseRunAccepts(spans.length, joined.length, piece.length))) break;
    if (joined) joined += ' ';
    const startAt = joined.length;
    joined += piece;
    spans.push({
      index: cursor,
      start: startAt,
      end: joined.length,
      baseFraction: cursor === index ? baseFraction : 0,
    });
    if ((segment.pauseAfterMs || 0) > 0 || ownClip(segment)) break;
    cursor += 1;
  }
  const end = spans[spans.length - 1]?.index ?? index;
  return { end, text: joined, pauseAfterMs: queue[end]?.pauseAfterMs || 0, spans };
}

function placeInRun(run: SpeechRun, audioFraction: number): { index: number; fraction: number } {
  const at = Math.min(0.999, Math.max(0, audioFraction)) * (run.text.length || 1);
  const span = run.spans.find((item) => at < item.end) || run.spans[run.spans.length - 1];
  if (!span) return { index: 0, fraction: audioFraction };
  const local = (at - span.start) / Math.max(1, span.end - span.start);
  const fraction = span.baseFraction > 0.04
    ? span.baseFraction + (1 - span.baseFraction) * local
    : local;
  return { index: span.index, fraction: Math.min(1, Math.max(0, fraction)) };
}

/** Onde a fila completa recomeça. O índice não volta a zero. */
function resumePoint(queue: SpeechSegment[], cursor?: SpeechCursor): {
  index: number;
  remainder?: string;
  baseFraction: number;
} {
  if (!cursor || !queue.length) return { index: 0, baseFraction: 0 };
  let index = cursor.index >= 0 && cursor.index < queue.length ? cursor.index : 0;
  if (cursor.section && queue[index]?.section !== cursor.section) {
    const matches = queue
      .map((segment, at) => (segment.section === cursor.section ? at : -1))
      .filter((at) => at >= 0);
    if (matches.length === 1) index = matches[0];
  }
  const fraction = cursor.fraction;
  if (fraction >= 0.97) {
    return { index: Math.min(index + 1, queue.length), baseFraction: 0 };
  }
  if (fraction <= 0.04) return { index, baseFraction: 0 };
  const remainder = sliceRemaining(queue[index].text, fraction);
  if (!remainder) return { index: Math.min(index + 1, queue.length), baseFraction: 0 };
  return { index, remainder, baseFraction: fraction };
}

function speakBrowserChunk(
  text: string,
  profile: GreeningVoiceProfile,
  onFraction?: (fraction: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || !window.speechSynthesis) {
      reject(new Error('browser_tts_unavailable'));
      return;
    }
    const synth = window.speechSynthesis;
    const voice = pickPortugueseVoice(synth.getVoices());
    if (!voice) {
      reject(new Error('browser_voice_missing'));
      return;
    }
    const spoken = utteredText(text, profile.id);
    const utterance = new SpeechSynthesisUtterance(spoken);
    utterance.lang = profile.lang;
    utterance.voice = voice;
    utterance.rate = profile.rate;
    startChunk(spoken);
    utterance.onboundary = (event) => {
      if (event.charIndex && spoken.length) onFraction?.(event.charIndex / spoken.length);
      if (!event.name || event.name === 'word') pulseBoundary();
    };
    utterance.onend = () => {
      endChunk();
      resolve();
    };
    utterance.onerror = () => reject(new Error('browser_tts_error'));
    synth.speak(utterance);
    if (synth.paused) synth.resume();
  });
}

/** Um player para o chat e para Ouvir análise. A voz nova só começa depois de silenciar a anterior. */
const pagePlayback: {
  audio: HTMLAudioElement | null;
  signal: { cancelled: boolean };
  halt: (() => void) | null;
} = {
  audio: null,
  signal: { cancelled: false },
  halt: null,
};
let pageRun = 0;

const playbackHold: { paused: boolean; waiters: Array<() => void> } = {
  paused: false,
  waiters: [],
};

function releaseHoldWaiters() {
  playbackHold.paused = false;
  const waiters = playbackHold.waiters.splice(0);
  waiters.forEach((resume) => resume());
}

function waitIfPaused(): Promise<void> {
  if (!playbackHold.paused) return Promise.resolve();
  return new Promise((resolve) => {
    playbackHold.waiters.push(resolve);
  });
}

function pausePlayback() {
  if (playbackHold.paused) return;
  playbackHold.paused = true;
  pagePlayback.audio?.pause();
  if (typeof window !== 'undefined' && window.speechSynthesis?.speaking) {
    window.speechSynthesis.pause();
  }
}

function resumePlayback() {
  if (!playbackHold.paused) return;
  playbackHold.paused = false;
  const waiters = playbackHold.waiters.splice(0);
  waiters.forEach((resume) => resume());
  const audio = pagePlayback.audio;
  if (audio?.paused) {
    audio.play().catch(() => undefined);
  }
  if (typeof window !== 'undefined' && window.speechSynthesis?.paused) {
    window.speechSynthesis.resume();
  }
}

function silencePlayback() {
  pageRun += 1;
  releaseHoldWaiters();
  pagePlayback.signal.cancelled = true;
  const audio = pagePlayback.audio;
  const halt = pagePlayback.halt;
  pagePlayback.audio = null;
  pagePlayback.halt = null;
  if (audio) audio.pause();
  halt?.();
  if (typeof window !== 'undefined' && window.speechSynthesis) window.speechSynthesis.cancel();
}

function playBlob(
  blob: Blob,
  text: string,
  signal: { cancelled: boolean },
  onFraction?: (fraction: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.cancelled) {
      reject(new Error('cancelled'));
      return;
    }
    const url = URL.createObjectURL(blob);
    const audio = new Audio(url);
    let settled = false;
    let halting = false;
    let giveUp = 0;
    let resumeTimer = 0;
    const finish = (error?: Error) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(giveUp);
      window.clearTimeout(resumeTimer);
      if (pagePlayback.audio === audio) {
        pagePlayback.audio = null;
        pagePlayback.halt = null;
      }
      window.clearInterval(watch);
      URL.revokeObjectURL(url);
      if (error) reject(error);
      else {
        endChunk();
        resolve();
      }
    };
    const halt = () => {
      halting = true;
      audio.pause();
      finish(new Error('cancelled'));
    };
    pagePlayback.audio = audio;
    pagePlayback.halt = halt;
    const watch = window.setInterval(() => {
      if (audio.duration) onFraction?.(audio.currentTime / audio.duration);
      if (!signal.cancelled) return;
      halt();
    }, 80);
    const clipMs = () => {
      if (!Number.isFinite(audio.duration) || audio.duration <= 0) return 20000;
      return audio.duration * 1000 + 800;
    };
    let started = false;
    const atEnd = () => audio.ended
      || (Number.isFinite(audio.duration) && audio.duration > 0 && audio.currentTime >= audio.duration - 0.2);
    const armGiveUp = () => {
      window.clearTimeout(giveUp);
      giveUp = window.setTimeout(() => {
        if (settled || playbackHold.paused || signal.cancelled) return;
        if (!started || !atEnd()) {
          armGiveUp();
          return;
        }
        finish();
      }, clipMs());
    };
    const startPlayback = () => {
      audio.play().catch((error) => {
        if (settled || halting || signal.cancelled) return;
        if (error instanceof DOMException && error.name === 'AbortError') return;
        finish(error instanceof Error ? error : new Error('audio_play_error'));
      });
    };
    audio.onplaying = () => {
      started = true;
    };
    audio.onended = () => finish();
    audio.onerror = () => finish(new Error('audio_play_error'));
    audio.onpause = () => {
      if (settled || halting || signal.cancelled || playbackHold.paused || atEnd()) return;
      window.clearTimeout(resumeTimer);
      resumeTimer = window.setTimeout(() => {
        if (settled || halting || signal.cancelled || playbackHold.paused || atEnd()) return;
        startPlayback();
      }, 40);
    };
    audio.onloadedmetadata = () => armGiveUp();
    if (signal.cancelled) {
      halt();
      return;
    }
    startChunk(text);
    armGiveUp();
    startPlayback();
  });
}

function fetchApiAudio(
  profile: GreeningVoiceProfile,
  text: string,
  tts: VoiceTtsAvailability | null,
): Promise<Blob> {
  if (!profile.proxyPath) return Promise.reject(new Error('tts_unavailable'));
  if (tts && !tts[profile.flagKey]) return Promise.reject(new Error('tts_unavailable'));
  return voiceApi.synthesize(profile.proxyPath, utteredText(text, profile.id));
}

/** TTS do avatar escolhido pelo clique; se indisponível/falhar → Greening3. */
export function useGreeningTts(onEnd?: () => void) {
  const onEndRef = useRef(onEnd);
  onEndRef.current = onEnd;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const audioSignal = useRef({ cancelled: false });
  const [activeDisplayName, setActiveDisplayName] = useState<string | null>(null);
  const [fallbackNotice, setFallbackNotice] = useState<string | null>(null);
  const [progress, setProgress] = useState<SpeechProgress | null>(null);
  const cursorRef = useRef<SpeechCursor>({ index: 0, fraction: 0, ratio: 0 });

  const stop = useCallback(() => {
    silencePlayback();
    audioSignal.current.cancelled = true;
    if (timerRef.current != null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    resetSpeechBands();
    setActiveDisplayName(null);
  }, []);

  const speak = useCallback((input: string | SpeechSegment[], options?: SpeakOptions) => {
    const chosenId = options?.profileId || getPreferredVoiceProfileId();
    if (chosenId === PADRAO3_BROWSER.id) return;
    const raw = typeof input === 'string' ? parseSpeechText(input, 0) : input;
    const source = raw.map((segment) => ({ ...segment, text: speakableBrand(segment.text) }));
    const intro = options?.intro ? speakableBrand(options.intro) : undefined;
    const browserQueue = expandKeepingSection(source);
    if (!browserQueue.length || typeof window === 'undefined') return;
    silencePlayback();
    if (options?.resume?.section) {
      cursorRef.current = options.resume;
      setProgress({
        index: options.resume.index,
        fraction: options.resume.fraction,
        ratio: options.resume.ratio,
        section: options.resume.section,
        total: 0,
        overall: options.resume.ratio,
      });
    } else {
      setProgress(null);
      if (!options?.resume) cursorRef.current = { index: 0, fraction: 0, ratio: 0 };
    }
    const runId = pageRun;
    const signal = { cancelled: false };
    pagePlayback.signal = signal;
    audioSignal.current = signal;
    if (timerRef.current != null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    resetSpeechBands();

    const preferred = getVoiceProfile(chosenId);
    const chain = fallbackChainFor(preferred.flagKey);
    const sentencePieces = options?.sentenceBuffer ? sentenceQueue(source) : [];
    const apiQueue = sentencePieces.length
      ? sentencePieces
      : preferred.engine === 'browser'
        ? browserQueue
        : apiQueueFromSegments(source);
    let queue = apiQueue.length ? apiQueue : browserQueue;
    if (!queue.length) queue = browserQueue;
    let start = resumePoint(queue, options?.resume);
    const clipText = (index: number) => (
      index === start.index && start.remainder ? start.remainder : (queue[index]?.text || '')
    );
    const heardFraction = (index: number, audioFraction: number) => {
      if (index === start.index && start.baseFraction > 0.04) {
        return start.baseFraction + (1 - start.baseFraction) * audioFraction;
      }
      return audioFraction;
    };
    setFallbackNotice(null);
    setActiveDisplayName(preferred.displayName);
    let locked: GreeningVoiceProfile | null = null;
    let prefetch: Promise<Blob> | null = null;
    let prefetchProfileId: string | null = null;
    let restartedOnBrowser = false;
    const runAt = (index: number) => speechRun(
      queue,
      index,
      index === start.index && start.remainder ? start.remainder : undefined,
      index === start.index ? start.baseFraction : 0,
    );

    const speakIntro = async (text: string, tts: VoiceTtsAvailability | null) => {
      if (preferred.engine === 'browser' || !preferred.proxyPath || (tts && !tts[preferred.flagKey])) {
        await speakBrowserChunk(text, PADRAO3_BROWSER);
        return;
      }
      const blob = await fetchApiAudio(preferred, text, tts);
      if (signal.cancelled) return;
      await playBlob(blob, text, signal);
    };

    const report = (index: number, fraction: number) => {
      if (runId !== pageRun) return;
      const section = queue[index]?.section;
      const ratio = sectionRatio(queue, index, fraction);
      const played = queue.slice(0, index).reduce((sum, segment) => sum + segment.text.length, 0);
      const totalLen = queue.reduce((sum, segment) => sum + segment.text.length, 0) || 1;
      const overall = (played + fraction * (queue[index]?.text.length || 0)) / totalLen;
      cursorRef.current = { index, fraction, ratio, section };
      setProgress({ index, fraction, ratio, section, total: queue.length, overall });
    };

    const step = async (index: number, tts: VoiceTtsAvailability | null) => {
      await waitIfPaused();
      if (runId !== pageRun) return;
      if (index >= queue.length) {
        resetSpeechBands();
        setProgress(null);
        onEndRef.current?.();
        return;
      }
      const text = clipText(index);
      report(index, heardFraction(index, 0));
      const tryOrder = locked ? [locked] : chain;
      let spoken = false;
      let advanceTo = index + 1;
      let pauseMs = Math.min(queue[index].pauseAfterMs || 0, 1000);
      for (const profile of tryOrder) {
        if (runId !== pageRun) return;
        const pending = profile.engine !== 'browser' && prefetchProfileId === profile.id ? prefetch : null;
        if (profile.engine !== 'browser') {
          prefetch = null;
          prefetchProfileId = null;
        }
        try {
          await waitIfPaused();
          if (runId !== pageRun || signal.cancelled) return;
          if (profile.engine === 'browser' || !profile.proxyPath) {
            await speakBrowserChunk(text, PADRAO3_BROWSER, (fraction) => report(index, heardFraction(index, fraction)));
          } else {
            const run = runAt(index);
            const blob = await (pending ?? fetchApiAudio(profile, run.text, tts));
            const nextAt = run.end + 1;
            if (nextAt < queue.length && profile.proxyPath) {
              const nextRun = runAt(nextAt);
              prefetch = fetchApiAudio(profile, nextRun.text, tts);
              prefetchProfileId = profile.id;
              void prefetch.catch(() => undefined);
            }
            if (signal.cancelled) throw new Error('cancelled');
            await waitIfPaused();
            if (runId !== pageRun || signal.cancelled) return;
            await playBlob(blob, run.text, signal, (fraction) => {
              const place = placeInRun(run, fraction);
              report(place.index, place.fraction);
            });
            advanceTo = nextAt;
            pauseMs = gapAfterRun(run.pauseAfterMs, nextAt < queue.length);
          }
          if (!locked) {
            locked = profile;
            setActiveDisplayName(profile.displayName);
            if (profile.id !== preferred.id) {
              setFallbackNotice(
                `${preferred.displayName} indisponível — lendo com ${profile.displayName}`,
              );
            } else {
              setFallbackNotice(null);
            }
          }
          spoken = true;
          break;
        } catch (error) {
          prefetch = null;
          if (runId !== pageRun || (error instanceof Error && error.message === 'cancelled')) return;
          resetSpeechBands();
          if (!locked && profile.engine !== 'browser' && !restartedOnBrowser) {
            restartedOnBrowser = true;
            queue = browserQueue.length ? browserQueue : queue;
            start = resumePoint(queue, options?.resume);
            locked = PADRAO3_BROWSER;
            setActiveDisplayName(PADRAO3_BROWSER.displayName);
            setFallbackNotice(
              `${preferred.displayName} indisponível — lendo com ${PADRAO3_BROWSER.displayName}`,
            );
            void step(start.index, tts);
            return;
          }
        }
      }
      if (!spoken || runId !== pageRun) {
        if (runId === pageRun) {
          resetSpeechBands();
          onEndRef.current?.();
        }
        return;
      }
      endChunk();
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        void (async () => {
          await waitIfPaused();
          if (runId !== pageRun) return;
          void step(advanceTo, tts);
        })();
      }, pauseMs);
    };

    const begin = (tts: VoiceTtsAvailability | null, firstAudio?: Promise<Blob> | null) => {
      prefetch = firstAudio ?? null;
      prefetchProfileId = firstAudio ? preferred.id : null;
      void step(start.index, tts);
    };

    if (preferred.engine === 'browser') {
      const runIntro = async () => {
        if (intro && !options?.resume) {
          try {
            await speakIntro(intro, null);
          } catch {
            if (runId !== pageRun) return;
          }
        }
        if (runId !== pageRun) return;
        begin(null);
      };
      void runIntro();
      return;
    }

    void (async () => {
      let tts: VoiceTtsAvailability = { greening1: false, greening2: false, greening3: true };
      let firstAudio: Promise<Blob> | null = null;
      try {
        const cfg = await voiceApi.config();
        tts = cfg.tts;
        if (preferred.proxyPath && queue[start.index] && (!tts || tts[preferred.flagKey])) {
          firstAudio = fetchApiAudio(preferred, runAt(start.index).text, tts);
          void firstAudio.catch(() => undefined);
        }
        if (intro && !options?.resume) {
          await speakIntro(intro, tts);
        }
        if (runId !== pageRun) return;
      } catch {
        /* sem config → trata 1/2 como indisponíveis e cai no Greening3 */
      }
      if (runId !== pageRun) return;
      begin(tts, firstAudio);
    })();
  }, []);

  useEffect(() => stop, [stop]);

  const getCursor = useCallback(() => cursorRef.current, []);

  return {
    speak,
    stop,
    pause: pausePlayback,
    resume: resumePlayback,
    getCursor,
    supported: typeof window !== 'undefined',
    activeDisplayName,
    fallbackNotice,
    progress,
  };
}

interface SpeakOptions {
  intro?: string;
  resume?: SpeechCursor;
  /** Voz escolhida neste clique. A corrida nova não relê a trava da anterior. */
  profileId?: string;
  /** Resposta do chat: cada frase é um áudio e a primeira espera a segunda. */
  sentenceBuffer?: boolean;
}

function sentenceQueue(source: SpeechSegment[]): SpeechSegment[] {
  const out: SpeechSegment[] = [];
  source.forEach((segment, index) => {
    speechSentences(segment.text).forEach((sentence, sentenceIndex) => {
      clauseSegments(sentence, 0).forEach((piece, pieceIndex) => {
        out.push({
          ...piece,
          section: `sent-${index}-${sentenceIndex}-${pieceIndex}`,
        });
      });
    });
  });
  return out;
}

function expandKeepingSection(segments: SpeechSegment[]): SpeechSegment[] {
  const out: SpeechSegment[] = [];
  for (const segment of segments) {
    expandSegments([segment]).forEach((piece) => out.push({ ...piece, section: segment.section }));
  }
  return out;
}

function sectionRatio(queue: SpeechSegment[], index: number, fraction: number): number {
  const section = queue[index]?.section;
  if (!section) return fraction;
  const group = queue.filter((segment) => segment.section === section);
  const total = group.reduce((sum, segment) => sum + segment.text.length, 0) || 1;
  let before = 0;
  for (const segment of queue) {
    if (segment === queue[index]) break;
    if (segment.section === section) before += segment.text.length;
  }
  return (before + fraction * (queue[index]?.text.length || 0)) / total;
}
